import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, Switch, FormControlLabel, Alert, Snackbar, Tooltip, Menu, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon,
  Person as PersonIcon, Email as EmailIcon, Lock as LockIcon, AdminPanelSettings as AdminIcon,
  Send as SendIcon, School as SchoolIcon
} from '@mui/icons-material';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/user.service';
import RoleService from '../../services/role.service';
import TenantService from '../../services/tenant.service';
import { useTranslation } from '../../hooks/useTranslation';

const UserManagement = () => {
  const { hasPermission, user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [returnToTenantUsers, setReturnToTenantUsers] = useState(false);
  const [rolesForTenant, setRolesForTenant] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendingWelcome, setSendingWelcome] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    tenant_id: '',
    is_active: true,
    roles: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // Open Edit User dialog when coming from Tenant Users (location.state.editUser)
  useEffect(() => {
    if (loading) return;
    const state = location.state;
    if (state?.editUser) {
      handleOpenDialog(state.editUser);
      setReturnToTenantUsers(state.returnTo === 'tenant-users');
      setShowTenantDropdown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [loading, location.state]);

  // Open Add User dialog when coming from Tenant Users (?add=1&returnTo=tenant-users) or normal (?add=1&tenant_id=5)
  useEffect(() => {
    if (loading) return;
    const add = searchParams.get('add');
    const tenantId = searchParams.get('tenant_id');
    const returnTo = searchParams.get('returnTo');
    if (add === '1') {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        tenant_id: tenantId || '',
        is_active: true,
        roles: []
      });
      setShowTenantDropdown(true);
      setReturnToTenantUsers(returnTo === 'tenant-users');
      setOpenDialog(true);
      setSearchParams({});
    }
  }, [loading, searchParams]);

  // When creating user for a tenant, load roles allowed for that tenant
  useEffect(() => {
    if (!showTenantDropdown || !formData.tenant_id || !openDialog) {
      setRolesForTenant([]);
      return;
    }
    let cancelled = false;
    TenantService.getRoles(formData.tenant_id)
      .then((res) => {
        if (!cancelled) setRolesForTenant(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setRolesForTenant([]);
      });
    return () => { cancelled = true; };
  }, [showTenantDropdown, formData.tenant_id, openDialog]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, tenantsRes] = await Promise.all([
        UserService.getAll(),
        RoleService.getAll(),
        TenantService.getAll()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      const tenantsData = tenantsRes.data?.data || tenantsRes.data || [];
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('userManagement.errorLoadingData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (user = null, preselectedTenantId = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        tenant_id: user.tenant_id ?? '',
        is_active: user.is_active ?? true,
        roles: user.roles?.map(role => role.id) || []
      });
      setShowTenantDropdown(false);
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        tenant_id: preselectedTenantId ?? '',
        is_active: true,
        roles: []
      });
      setShowTenantDropdown(Boolean(preselectedTenantId));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setShowTenantDropdown(false);
    setReturnToTenantUsers(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      tenant_id: '',
      is_active: true,
      roles: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        is_active: formData.is_active,
        roles: formData.roles
      };

      if (showTenantDropdown && formData.tenant_id) {
        userData.tenant_id = formData.tenant_id;
      } else if (!editingUser && currentUser?.tenant_id) {
        userData.tenant_id = currentUser.tenant_id;
      }

      if (formData.password) {
        userData.password = formData.password;
      }

      console.log('Sending user data:', userData);

      if (editingUser) {
        await UserService.update(editingUser.id, userData);
        showSnackbar(t('userManagement.userUpdatedSuccess'));
        if (returnToTenantUsers) {
          navigate('/admin/tenant-users');
          handleCloseDialog();
          return;
        }
      } else {
        await UserService.create(userData);
        showSnackbar(t('userManagement.userCreatedSuccess'));
        if (returnToTenantUsers) {
          navigate('/admin/tenant-users');
          handleCloseDialog();
          return;
        }
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving user:', error);
      
      // Extract detailed error message from response
      let errorMessage = t('userManagement.errorSavingUser');
      if (error.response?.data?.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm(t('userManagement.confirmDeleteUser'))) {
      try {
        await UserService.delete(userId);
        showSnackbar(t('userManagement.userDeletedSuccess'));
        loadData();
      } catch (error) {
        console.error('Error deleting user:', error);
        showSnackbar(t('userManagement.errorDeletingUser'), 'error');
      }
    }
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleSendWelcomeEmail = async () => {
    if (!selectedUser) return;
    setSendingWelcome(true);
    try {
      await UserService.sendWelcomeEmail(selectedUser.id);
      showSnackbar(t('userManagement.welcomeEmailSentSuccess'));
      handleMenuClose();
    } catch (error) {
      console.error('Error sending welcome email:', error);
      showSnackbar(error.response?.data?.message || t('userManagement.failedSendWelcomeEmail'), 'error');
    } finally {
      setSendingWelcome(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>{t('userManagement.loadingUsers')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              {t('userManagement.title')}
            </Typography>
            {hasPermission('users.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('userManagement.addUser')}
              </Button>
            )}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('tenantUsers.email')}</TableCell>
                  <TableCell>{t('tenantUsers.roles')}</TableCell>
                  <TableCell>{t('tenantUsers.status')}</TableCell>
                  <TableCell>{t('tenantUsers.lastLogin')}</TableCell>
                  <TableCell>{t('tenantUsers.createdAt')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon color="action" />
                        <Typography variant="body2">{user.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon color="action" fontSize="small" />
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {user.roles?.map((role) => (
                          <Chip
                            key={role.id}
                            label={role.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? t('common.active') : t('common.inactive')}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.last_login_at ? formatDate(user.last_login_at) : t('tenantUsers.never')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('userManagement.moreActions')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingUser ? t('userManagement.editUser') : t('userManagement.createUser')}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label={t('common.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label={t('tenantUsers.email')}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
              />
              {showTenantDropdown && (
                <FormControl fullWidth>
                  <InputLabel>{t('tenantUsers.tenant')}</InputLabel>
                  <Select
                    value={formData.tenant_id || ''}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    label={t('tenantUsers.tenant')}
                  >
                    <MenuItem value="">{t('userManagement.noTenant')}</MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                label={t('userManagement.password')}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                fullWidth
                helperText={editingUser ? t('userManagement.passwordLeaveEmpty') : ''}
              />
              <FormControl fullWidth>
                <InputLabel>{t('tenantUsers.roles')}</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={(e) => setFormData({ ...formData, roles: e.target.value })}
                  renderValue={(selected) => {
                    const availableRoles = showTenantDropdown && rolesForTenant.length > 0 ? rolesForTenant : roles;
                    return (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {selected.map((value) => {
                          const role = availableRoles.find(r => r.id === value);
                          return (
                            <Chip key={value} label={role?.name} size="small" />
                          );
                        })}
                      </Box>
                    );
                  }}
                >
                  {(showTenantDropdown && rolesForTenant.length > 0 ? rolesForTenant : roles).map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {showTenantDropdown && rolesForTenant.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('userManagement.noRolesForTenant')}
                  </Typography>
                )}
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label={t('userManagement.active')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingUser ? t('userManagement.update') : t('userManagement.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission('users.edit') && (
          <MenuItem onClick={() => {
            handleOpenDialog(selectedUser);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}
        {hasPermission('users.edit') && (
          <MenuItem onClick={() => { handleSendWelcomeEmail(); handleMenuClose(); }} disabled={sendingWelcome}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('userManagement.sendWelcomeEmail')}</ListItemText>
          </MenuItem>
        )}
        {hasPermission('attendances.manage') && (
          <MenuItem onClick={() => {
            navigate(`/attendance/assignments?user_id=${selectedUser?.id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <SchoolIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('teacherAssignments.title')}</ListItemText>
          </MenuItem>
        )}
        {hasPermission('users.delete') && (
          <MenuItem onClick={() => {
            handleDelete(selectedUser.id);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
